// Borrower magic-link landing — redeems the invite token, signs the borrower in,
// then prompts them to set a password if it's their first visit.
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, errorMessage, setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { toast } from "sonner";

export default function BorrowerInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("redeeming"); // redeeming | set_pw | error | done
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    (async () => {
      if (!token) {
        setErr("This link is missing a token. Please use the link from your invite email.");
        setState("error");
        return;
      }
      try {
        const { data } = await api.post("/auth/invite/redeem", { token });
        if (data.access_token) setToken(data.access_token);
        setName(data.name || "");
        await refresh();
        if (data.needs_password_setup) {
          setState("set_pw");
        } else {
          nav("/borrower", { replace: true });
        }
      } catch (e) {
        setErr(errorMessage(e));
        setState("error");
      }
    })();
  }, [token]);

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { password: pw });
      toast.success("Password set");
      await refresh();
      nav("/borrower", { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo height={72} />
        </div>
        <Card className="border-slate-800 bg-slate-900 p-6">
          {state === "redeeming" && (
            <div className="flex items-center justify-center gap-3 py-10 text-sm text-slate-400" data-testid="invite-redeeming">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
              Opening your borrower portal…
            </div>
          )}

          {state === "error" && (
            <div data-testid="invite-error" className="space-y-4">
              <div className="text-lg font-semibold text-slate-100">We couldn&apos;t open this link</div>
              <div className="rounded-md border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                {err}
              </div>
              <div className="text-sm text-slate-400">
                The invite link may have expired or already been used. Try{" "}
                <Link to="/login" className="text-emerald-300 hover:text-emerald-200">signing in</Link>
                {" "}directly, or ask your broker to send a new application.
              </div>
            </div>
          )}

          {state === "set_pw" && (
            <form onSubmit={submitPw} className="space-y-4" data-testid="invite-set-pw-form">
              <div>
                <div className="text-xl font-semibold text-slate-100">Welcome{name ? `, ${name}` : ""}</div>
                <div className="mt-1 text-sm text-slate-400">
                  Set a password to secure your borrower portal. You can sign back in any time at{" "}
                  <Link to="/login" className="text-emerald-300">/login</Link>.
                </div>
              </div>
              <div>
                <Label className="text-slate-300">New password</Label>
                <Input
                  type="password"
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  minLength={6}
                  required
                  className="mt-1 border-slate-800 bg-slate-950 text-slate-50"
                  data-testid="invite-pw-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Confirm password</Label>
                <Input
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  minLength={6}
                  required
                  className="mt-1 border-slate-800 bg-slate-950 text-slate-50"
                  data-testid="invite-pw2-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                data-testid="invite-submit-btn"
              >
                {busy ? "Saving…" : "Set password & continue"}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
